import { executeGraphqlRequest, GraphqlRequestError } from '../apollo/graphql-request';

export type ReviewChannel = 'LINK' | 'EMAIL';
export type ReviewHiddenReason = 'SPAM' | 'ABUSE' | 'FAKE' | 'NOT_ABOUT_PROVIDER';
export type ReviewReportState = 'OPEN' | 'REJECTED' | 'UPHELD';

export const REVIEW_HIDDEN_REASONS: ReviewHiddenReason[] = [
  'SPAM',
  'ABUSE',
  'FAKE',
  'NOT_ABOUT_PROVIDER',
];

export interface ModeratedReview {
  uuid: string;
  profileUuid: string;
  profile: {
    uuid: string;
    slug?: string | null;
    title: string;
    firstName?: string | null;
    lastName?: string | null;
  } | null;
  authorName: string;
  stars: number;
  text: string;
  channel: ReviewChannel;
  socialUrl?: string | null;
  eventType?: string | null;
  eventDate?: string | null;
  createdAt: string;
  hiddenAt?: string | null;
  hiddenReason?: ReviewHiddenReason | null;
  reply: { text: string; updatedAt: string } | null;
  report: {
    state: ReviewReportState;
    message?: string | null;
    createdAt: string;
    decidedAt?: string | null;
  } | null;
}

export interface ModeratedReviewPage {
  items: ModeratedReview[];
  total: number;
}

export interface ReviewModerationFilter {
  profileUuid?: string;
  stars?: number;
  hidden?: boolean;
  reported?: boolean;
}

const MODERATED_REVIEW_SELECTION_SET = `
  uuid
  profileUuid
  profile {
    uuid
    slug
    title
    firstName
    lastName
  }
  authorName
  stars
  text
  channel
  socialUrl
  eventType
  eventDate
  createdAt
  hiddenAt
  hiddenReason
  reply {
    text
    updatedAt
  }
  report {
    state
    message
    createdAt
    decidedAt
  }
`;

const MODERATION_REVIEWS_QUERY = `
  query ModerationReviews($filter: ReviewModerationFilterInput, $limit: Int!, $offset: Int!) {
    moderationReviews(filter: $filter, limit: $limit, offset: $offset) {
      total
      items {
        ${MODERATED_REVIEW_SELECTION_SET}
      }
    }
  }
`;

const MODERATION_OPEN_REVIEW_REPORTS_QUERY = `
  query ModerationOpenReviewReports($limit: Int!, $offset: Int!) {
    moderationOpenReviewReports(limit: $limit, offset: $offset) {
      total
      items {
        ${MODERATED_REVIEW_SELECTION_SET}
      }
    }
  }
`;

const MODERATION_OPEN_REVIEW_REPORTS_COUNT_QUERY = `
  query ModerationOpenReviewReportsCount {
    moderationOpenReviewReports(limit: 1, offset: 0) {
      total
    }
  }
`;

const REVIEW_HIDE_MUTATION = `
  mutation ReviewHide($reviewUuid: ID!, $reason: ReviewHiddenReason!) {
    reviewHide(reviewUuid: $reviewUuid, reason: $reason) {
      ${MODERATED_REVIEW_SELECTION_SET}
    }
  }
`;

const REVIEW_REPORT_REJECT_MUTATION = `
  mutation ReviewReportReject($reviewUuid: ID!) {
    reviewReportReject(reviewUuid: $reviewUuid) {
      ${MODERATED_REVIEW_SELECTION_SET}
    }
  }
`;

// Drops unset fields, so an empty filter lists every Review.
export function toModerationFilterInput(
  filter: ReviewModerationFilter
): ReviewModerationFilter | null {
  const input: ReviewModerationFilter = {};
  if (filter.profileUuid) {
    input.profileUuid = filter.profileUuid;
  }
  if (typeof filter.stars === 'number') {
    input.stars = filter.stars;
  }
  if (typeof filter.hidden === 'boolean') {
    input.hidden = filter.hidden;
  }
  if (typeof filter.reported === 'boolean') {
    input.reported = filter.reported;
  }

  return Object.keys(input).length > 0 ? input : null;
}

// The API refuses moderation to Accounts outside MODERATOR_ACCOUNT_UUIDS with a 403.
export function isModerationForbiddenError(error: unknown): boolean {
  return (
    error instanceof GraphqlRequestError &&
    (error.statusCode === 403 || error.code === 'FORBIDDEN')
  );
}

export async function fetchModerationReviews(
  filter: ReviewModerationFilter,
  limit: number,
  offset: number,
  accessToken?: string
): Promise<ModeratedReviewPage> {
  const data = await executeGraphqlRequest<{ moderationReviews: ModeratedReviewPage }>(
    MODERATION_REVIEWS_QUERY,
    { filter: toModerationFilterInput(filter), limit, offset },
    { accessToken, requestName: 'ModerationReviews' }
  );

  return data.moderationReviews;
}

export async function fetchOpenReviewReports(
  limit: number,
  offset: number,
  accessToken?: string
): Promise<ModeratedReviewPage> {
  const data = await executeGraphqlRequest<{ moderationOpenReviewReports: ModeratedReviewPage }>(
    MODERATION_OPEN_REVIEW_REPORTS_QUERY,
    { limit, offset },
    { accessToken, requestName: 'ModerationOpenReviewReports' }
  );

  return data.moderationOpenReviewReports;
}

export async function fetchOpenReviewReportsCount(accessToken?: string): Promise<number> {
  const data = await executeGraphqlRequest<{ moderationOpenReviewReports: { total: number } }>(
    MODERATION_OPEN_REVIEW_REPORTS_COUNT_QUERY,
    {},
    { accessToken, requestName: 'ModerationOpenReviewReportsCount' }
  );

  return data.moderationOpenReviewReports.total;
}

export async function hideReviewRequest(
  reviewUuid: string,
  reason: ReviewHiddenReason,
  accessToken?: string
): Promise<ModeratedReview> {
  const data = await executeGraphqlRequest<{ reviewHide: ModeratedReview }>(
    REVIEW_HIDE_MUTATION,
    { reviewUuid, reason },
    { accessToken, requestName: 'ReviewHide' }
  );

  return data.reviewHide;
}

export async function rejectReviewReportRequest(
  reviewUuid: string,
  accessToken?: string
): Promise<ModeratedReview> {
  const data = await executeGraphqlRequest<{ reviewReportReject: ModeratedReview }>(
    REVIEW_REPORT_REJECT_MUTATION,
    { reviewUuid },
    { accessToken, requestName: 'ReviewReportReject' }
  );

  return data.reviewReportReject;
}
